import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * A company's own mark: a carrier, or the rater the agency quotes through.
 *
 * The files live in `public/brand/quoting/`, named by the company's key —
 * `ezlynx.png`, `progressive.png`. Drop a file in and it shows; nothing here
 * changes.
 *
 * Each is a square badge, as a company shows itself on its own profile, with
 * its own ground colour filling the tile to the rounded corners. So a row or
 * a column of them is a set of equal tiles, whatever each logo's shape. Until
 * a company has a file, its initials stand in on a tile the same size.
 */
export function BrandLogo({ id, name, size = 28 }: { id: string | null | undefined; name: string; size?: number }) {
  // Try the sharp one first, then the picture, then give up and write the initials.
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => { setAttempt(0); }, [id]);
  const file = id && attempt < 2 ? `/brand/quoting/${id}.${attempt === 0 ? 'svg' : 'png'}` : null;
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  return (
    <Box
      aria-hidden
      sx={(t) => ({
        width: size,
        height: size,
        flexShrink: 0,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: `${t.ds.radius.sm}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: file ? t.palette.common.white : t.tokens.brand.primarySofter,
        color: t.tokens.brand.primary,
      })}
    >
      {file ? (
        <Box component="img" src={file} alt="" onError={() => setAttempt((n) => n + 1)}
          sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>{initials}</Typography>
      )}
    </Box>
  );
}
